import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import { useHaptics } from "@/hooks/use-haptics";
import { useSwipeTabs } from "@/hooks/use-swipe-tabs";
import SwipeAnimatedContent from "@/components/SwipeAnimatedContent";
import TabDots from "@/components/TabDots";
import {
  floorJoistHeaders, floorJoistRows,
  steelBeamHeaders, steelBeamRows,
  headerSpanHeaders, headerSpanRows,
  anchorHeaders, anchorRows,
  seismicBraceHeaders, seismicBraceRows,
  designLoadHeaders, designLoadRows,
} from "@/components/structural/structural-data";
import { SeismicForceCalc, AnchorBoltCalc, BeamLoadCalc, DeflectionCalc, SdcLookup } from "@/components/structural/StructuralCalcs";
import TradeFormulas from "@/components/TradeFormulas";

const STRUCTURAL_CATEGORIES = [
  {
    id: "beams",
    label: "Beams & Loads",
    color: "bg-primary",
    tools: [
      { value: "joists", label: "Joists" },
      { value: "steelbeam", label: "Steel" },
      { value: "headers", label: "Headers" },
      { value: "beamcalc", label: "Load Calc" },
      { value: "deflection", label: "Deflect." },
      { value: "loads", label: "Loads" },
    ],
  },
  {
    id: "anchors",
    label: "Anchors",
    color: "bg-copper",
    tools: [
      { value: "anchors", label: "All Anchors" },
      { value: "anchorcalc", label: "Sizing" },
    ],
  },
  {
    id: "seismic",
    label: "Seismic",
    color: "bg-safety",
    tools: [
      { value: "seismicforce", label: "Fp Calc" },
      { value: "bracing", label: "Bracing" },
      { value: "sdc", label: "SDC" },
      { value: "formulas", label: "Formulas" },
    ],
  },
];

const Structural = () => {
  const { lightTap } = useHaptics();
  const [searchParams] = useSearchParams();

  const requestedTool = searchParams.get("tool");
  const findCategoryForTool = (toolValue: string) =>
    STRUCTURAL_CATEGORIES.find(c => c.tools.some(t => t.value === toolValue));
  const initialCat = requestedTool ? findCategoryForTool(requestedTool) : null;

  const [category, setCategory] = useState(initialCat?.id ?? "beams");
  const [activeTool, setActiveTool] = useState(requestedTool ?? "joists");
  const currentCategory = STRUCTURAL_CATEGORIES.find(c => c.id === category)!;

  useEffect(() => {
    if (requestedTool) {
      const cat = findCategoryForTool(requestedTool);
      if (cat) { setCategory(cat.id); setActiveTool(requestedTool); }
    }
  }, [requestedTool]);

  const handleCategoryChange = (catId: string) => {
    lightTap();
    setCategory(catId);
    const cat = STRUCTURAL_CATEGORIES.find(c => c.id === catId)!;
    setActiveTool(cat.tools[0].value);
  };

  const handleToolChange = useCallback((tool: string) => { lightTap(); setActiveTool(tool); }, [lightTap]);

  const toolValues = currentCategory.tools.map(t => t.value);
  const handleBoundarySwipe = useCallback((dir: "left" | "right") => {
    const catIdx = STRUCTURAL_CATEGORIES.findIndex(c => c.id === category);
    if (dir === "left" && catIdx < STRUCTURAL_CATEGORIES.length - 1) {
      const next = STRUCTURAL_CATEGORIES[catIdx + 1];
      lightTap(); setCategory(next.id); setActiveTool(next.tools[0].value);
      toast(next.label, { duration: 1200 });
    } else if (dir === "right" && catIdx > 0) {
      const prev = STRUCTURAL_CATEGORIES[catIdx - 1];
      lightTap(); setCategory(prev.id); setActiveTool(prev.tools[prev.tools.length - 1].value);
      toast(prev.label, { duration: 1200 });
    }
  }, [category, lightTap]);
  const swipeHandlers = useSwipeTabs({ tabs: toolValues, activeTab: activeTool, onChangeTab: handleToolChange, onBoundarySwipe: handleBoundarySwipe });

  const renderTool = () => {
    const structFav = (category: string) => ({
      type: "structural" as const,
      category,
      makeId: (row: (string | number)[]) => `struct-${category}-${String(row[0]).replace(/\W/g, "")}`,
      makeName: (row: (string | number)[]) => String(row[0]),
      makeDetail: (row: (string | number)[]) => row.slice(1, 3).join(" · "),
    });
    switch (activeTool) {
      case "joists": return <div className="-mx-4"><DataTable headers={floorJoistHeaders} rows={floorJoistRows} searchable searchPlaceholder="Search joist spans…" favoritesConfig={structFav("🏗️ Joist Spans")} /></div>;
      case "steelbeam": return <div className="-mx-4"><DataTable headers={steelBeamHeaders} rows={steelBeamRows} searchable searchPlaceholder="Search steel beams…" favoritesConfig={structFav("🏗️ Steel Beams")} /></div>;
      case "headers": return <div className="-mx-4"><DataTable headers={headerSpanHeaders} rows={headerSpanRows} searchable searchPlaceholder="Search header spans…" favoritesConfig={structFav("🏗️ Header Spans")} /></div>;
      case "beamcalc": return <BeamLoadCalc />;
      case "deflection": return <DeflectionCalc />;
      case "loads": return <div className="-mx-4"><DataTable headers={designLoadHeaders} rows={designLoadRows} searchable searchPlaceholder="Search design loads…" favoritesConfig={structFav("🏗️ Design Loads")} /></div>;
      case "anchors": return <div className="-mx-4"><DataTable headers={anchorHeaders} rows={anchorRows} searchable searchPlaceholder="Search anchors…" favoritesConfig={structFav("🏗️ Anchors")} /></div>;
      case "anchorcalc": return <AnchorBoltCalc />;
      case "seismicforce": return <SeismicForceCalc />;
      case "bracing": return <div className="-mx-4"><DataTable headers={seismicBraceHeaders} rows={seismicBraceRows} searchable searchPlaceholder="Search bracing intervals…" favoritesConfig={structFav("🏗️ Seismic Bracing")} /></div>;
      case "sdc": return <SdcLookup />;
      case "formulas": return <TradeFormulas trade="structural" />;
      default: return null;
    }
  };

  return (
    <div className="pb-24 animate-fade-in">
      <PageHeader title="🏗️ Structural" subtitle="Beam spans, anchors, seismic bracing & load tables" />
      <div className="px-4 mt-2">
        <div className="flex gap-1.5 bg-secondary rounded-lg p-1">
          {STRUCTURAL_CATEGORIES.map(cat => (
            <button key={cat.id} onClick={() => handleCategoryChange(cat.id)}
              className={`flex-1 py-2 px-2 rounded-md text-xs font-semibold transition-all ${category === cat.id ? `${cat.color} text-primary-foreground shadow-sm` : "text-muted-foreground hover:text-foreground"}`}>
              {cat.label}
            </button>
          ))}
        </div>
        <div className="flex gap-1 mt-2 bg-secondary/50 rounded-lg p-1 overflow-x-auto">
          {currentCategory.tools.map(tool => (
            <button key={tool.value} onClick={() => swipeHandlers.changeTab(tool.value)}
              className={`flex-1 py-1.5 px-2 rounded-md text-xs font-medium transition-all whitespace-nowrap ${activeTool === tool.value ? "bg-card text-foreground shadow-sm border border-border" : "text-muted-foreground hover:text-foreground"}`}>
              {tool.label}
            </button>
          ))}
        </div>
      </div>
      <div className="px-4 mt-3" {...{onTouchStart: swipeHandlers.onTouchStart, onTouchMove: swipeHandlers.onTouchMove, onTouchEnd: swipeHandlers.onTouchEnd}}>
        <SwipeAnimatedContent activeKey={activeTool} swipeDirection={swipeHandlers.swipeDirection} edgeBounce={swipeHandlers.edgeBounce} onAnimationDone={swipeHandlers.clearDirection} onEdgeBounceDone={swipeHandlers.clearEdgeBounce}>
          {renderTool()}
        </SwipeAnimatedContent>
        <TabDots total={toolValues.length} activeIndex={toolValues.indexOf(activeTool)} onDotClick={(i) => swipeHandlers.changeTab(toolValues[i])} />
      </div>
    </div>
  );
};

export default Structural;
